import { useEffect, useRef, useState } from "react";
import { CONTACT_EMAIL, slug } from "../../lib/site";
import { GOLD, GOLD_DEEP, GoldBtn, HAIR, IVORY, SLATE, Serif } from "./kit";

const LINE = { borderColor: "rgba(233,214,162,0.20)", color: IVORY };
const INPUT = "w-full bg-transparent border-b py-2.5 outline-none text-[15.5px] transition-colors focus:border-[#e9d6a2]";

const Choice = ({ field, value, onChange, prefix }) => {
  const picked = field.multi ? value || [] : value;
  const toggle = (o) => onChange(field.multi ? (picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o]) : o);
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {field.options.map((o) => {
        const on = field.multi ? picked.includes(o) : picked === o;
        return (
          <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${prefix}-${field.key}-${slug(o)}`} aria-pressed={on}
            className="text-[10.5px] tracking-[0.18em] uppercase px-3.5 py-2 rounded-full transition-colors"
            style={on ? { background: GOLD, color: "#1a1408", border: "1px solid transparent" } : { color: SLATE, border: "1px solid rgba(233,214,162,0.24)" }}>
            {o}
          </button>
        );
      })}
    </div>
  );
};

const Field = ({ field, value, onChange, prefix }) => (
  <div className={field.wide ? "md:col-span-2" : ""}>
    <label className="block text-[10px] tracking-[0.28em] uppercase" style={{ color: SLATE }}>
      {field.label}{field.optional && <span style={{ color: GOLD_DEEP }}> · optional</span>}
    </label>
    {field.options ? (
      <Choice field={field} value={value} onChange={onChange} prefix={prefix} />
    ) : field.type === "textarea" ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} required={!field.optional} maxLength={2000} data-testid={`${prefix}-${field.key}`} className={`${INPUT} resize-none`} style={LINE} />
    ) : (
      <input type={field.type || "text"} value={value || ""} onChange={(e) => onChange(e.target.value)} required={!field.optional} maxLength={300} data-testid={`${prefix}-${field.key}`} className={INPUT} style={LINE} />
    )}
  </div>
);

export default function InquiryForm({ form, fields, submitLabel, testId, thanks }) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState("idle");
  const doneRef = useRef(null);
  useEffect(() => {
    if (status === "sent") doneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [status]);
  const set = (k) => (v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.options && !f.optional && !(f.multi ? values[f.key]?.length : values[f.key]));
    if (missing) return setStatus(`Please choose: ${missing.label}`);
    setStatus("sending");
    const answers = Object.fromEntries(
      fields.filter((f) => !["name", "email"].includes(f.key)).map((f) => [f.label, [values[f.key] || ""].flat().join(", ")])
    );
    try {
      const res = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ form, name: values.name || "", email: values.email || "", fields: answers, website: e.target.website.value }),
      });
      if (!res.ok) throw new Error();
      setStatus("sent");
      setValues({});
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent")
    return (
      <div ref={doneRef} className="mt-12 py-14 text-center" style={{ borderTop: HAIR, borderBottom: HAIR }} data-testid={`${testId}-confirmation`}>
        <p style={{ ...Serif, color: GOLD, fontSize: "clamp(1.4rem, 2.4vw, 1.8rem)" }}>Thank you.</p>
        <p className="mt-4 text-[15.5px] leading-[1.7]" style={{ color: IVORY }}>{thanks}</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-9" data-testid={testId}>
      {fields.map((f) => <Field key={f.key} field={f} value={values[f.key]} onChange={set(f.key)} prefix={testId} />)}
      <input name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <div className="md:col-span-2 pt-2 text-center">
        <GoldBtn type="submit" testId={`${testId}-submit`}>{status === "sending" ? "Sending…" : submitLabel}</GoldBtn>
        {status.startsWith("Please") && <p className="mt-5 text-[13px]" style={{ color: "#e8a3a3" }} data-testid={`${testId}-error`}>{status}</p>}
        {status === "error" && (
          <p className="mt-5 text-[13px]" style={{ color: "#e8a3a3" }} data-testid={`${testId}-error`}>
            Something went wrong. Please try again, or write to <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-4">{CONTACT_EMAIL}</a>.
          </p>
        )}
      </div>
    </form>
  );
}
